// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { HeaderService } from 'src/app/header.service';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { AdminSettingsService } from 'src/services/admin-settings.service';
import { Observable } from 'rxjs';
import { UserService } from 'src/services/user-service';
import { IAdminSettings } from 'src/types/admin-settings';
import {finalize, map, switchMap} from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../dialogs/alert/alert.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../services/notification-service';
import { SecurityRequestService } from '../../../services/security-request.service';
import authHelper from '../../auth/authHelper';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '../../shared/validators/custom-validators';
import { IGroup, OrgType, Require2FAType } from '../../../types/group';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {loadOrganizations} from '../../store/organizations/organizations.actions';
import { ActivatedRoute } from '@angular/router';
import { MatRipple } from '@angular/material/core';
import {selectUser} from '../../store/user/user.selectors';
import {StripeProductTier} from '../../../types/subscription';
import {D3eService} from '../../../services/d3e-service';
import { DeactivatableComponent } from '../../guards/page.guard';
import { SecurityApprovalMethod } from '../../../types/security-requests';
import { IRestConfig } from '../../../types/config';
import { RestConfigService } from '../../../services/config-service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-admin-settings',
    templateUrl: './admin-settings.component.html',
    styleUrls: ['./admin-settings.component.scss'],
    standalone: false
})
export class AdminSettingsComponent implements OnInit, DeactivatableComponent {
  @ViewChild(MatRipple, {static: false}) ripple: MatRipple;
  selectedOrgId: string;
  selectedOrg: IGroup;
  settingsDataLoaded = false;
  adminSettings: IAdminSettings = null;
  allAdminSettingsViewable = false;
  enableSubscriptionKey = false;
  enableRecoveryKey = false;
  requireKeyPassword = false;
  enableSignOnRestriction = true; // Caution: This is the opposite of the REST API value
  isDirty = false;
  showAutoImportButton = false;
  userCanClearSensorFeatureFlag = false;
  enableAutoImport = true;
  showRemovableStorage = false;
  enableRemovableStorage = false;
  enableClearRemovableStorage = false;
  showEnableClearRemovableStorage = false;
  enableDataDeception = false;
  showEnableDataDeception = false;
  enableClearDataDeception = false;
  showEnableClearDataDeception = false;
  enableNetworkDeception = false;
  showEnableNetworkDeception = false;
  enableClearNetworkDeception = false;
  showEnableClearNetworkDeception = false;
  enableTrustedNetwork = false;
  showEnableTrustedNetwork = false;
  enableClearTrustedNetwork = false;
  showEnableClearTrustedNetwork = false;
  enableAVTethering = true;
  enableMfaOtp = false;
  showEnableMfaOtp = false;
  enableMfaFinger = false;
  showEnableMfaFinger = false;
  enableMfaFace = false;
  showEnableMfaFace = false;
  enableMfaCac = false;
  showEnableMfaCac = false;
  enableAutoUpdate = false;
  showEnableAutoUpdate = false;
  enableClientUpdate = false;
  enableSingleUserMode = false;
  enableSsdPwdBackup = false;
  showEnableSingleUserMode = false;
  enableLimitAuthTrys = false;
  showEnableLimitAuthTrys = false;
  showDeleteOrgButton = false;
  loginAttempts = 0;
  pwExpInterval = 0;
  mfaPwExpInterval = 0;
  endpointInactiveIntervalDefault = 180;
  endpointInactiveInterval: number | null = 180;
  useInactiveSystemDefault = true;
  endpointInactiveIntervalMin = 30;
  endpointInactiveIntervalMax = 10000;
  disableSubmit = false;

  fNone: string;
  fCancel: string;
  fSubmit: string;
  fSuccess: any;
  fDisableKeyTitle: string;
  fDisableKeyContent: string;
  fYes: string;
  fNo: string;

  title: string;
  @ViewChild('enableSubscriptionKeyControl') enableSubscriptionKeyControl: MatSlideToggle;
  orgName = 'Organization Name';
  orgFormGroup: UntypedFormGroup;
  contactEmail = 'testcustomer@testcompany.test';
  requireTwoFactor = Require2FAType.Optional;
  required2FaType = Require2FAType;
  selectedTab = 0;
  securityApprovalMethod = SecurityApprovalMethod.PERCENT_RULE;
  securityApprovalMethodType = SecurityApprovalMethod;
  restConfig$!: Observable<IRestConfig>;

  haveDDTier$: Observable<boolean> = this.store.select(selectUser()).pipe(
    switchMap(user => this.d3eService.get_subscriptions(user?.group_id?.toString())),
    map(subscriptions => subscriptions
      .filter(s => s.stripe_product.tier == StripeProductTier.PERSONAL || s.stripe_product.tier == StripeProductTier.BUSINESS).length > 0)
  );

  constructor(
      private headerService: HeaderService,
      private adminSettingsService: AdminSettingsService,
      private userService: UserService,
      private dialog: MatDialog,
      private securityRequestService: SecurityRequestService,
      private translate: TranslateService,
      private notification: NotificationService,
      private formBuilder: UntypedFormBuilder,
      private readonly store: Store<AppState>,
      private readonly d3eService: D3eService,
      private route: ActivatedRoute,
      private readonly restConfigService: RestConfigService
  ) {
    headerService.setContextHelp([
      'help.settings'
    ]);
    this.translate.get(['ADMS_NOSETTINGS', 'TITLE_SETTINGS', 'CANCEL', 'SUBMIT', 'ADMS_SETTINGS_UPDATED',
      'DISABLE_SUB_KEY_TITLE', 'YES', 'NO', 'DISABLE_SUB_KEY_CONTENT', 'SELECTOR_YOUR_SETTINGS'])
        .subscribe(vals => {
      this.fNone = vals['ADMS_NOSETTINGS'];
      this.fCancel = vals['CANCEL'];
      this.fSubmit = vals['SUBMIT'];
      this.fSuccess = vals['ADMS_SETTINGS_UPDATED'];
      this.title = vals['TITLE_SETTINGS'];
      this.fDisableKeyTitle = vals['DISABLE_SUB_KEY_TITLE'];
      this.fDisableKeyContent = vals['DISABLE_SUB_KEY_CONTENT'];
      this.fYes = vals['YES'];
      this.fNo = vals['NO'];

      this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_YOUR_SETTINGS']);
    });

    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-cog');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        this.getSelectedOrg();
    });


    this.showAutoImportButton = authHelper.getFeatureFlagFromToken('aib') || false;

    this.userCanClearSensorFeatureFlag = authHelper.getFeatureFlagFromToken('ucc') ?? false;

    this.orgFormGroup = this.formBuilder.group({
      orgName: [ this.orgName, [Validators.required, Validators.maxLength(255)]],
      contactEmail: [this.contactEmail, [Validators.required, CustomValidators.maxByteLength(255),
        Validators.pattern(CustomValidators.emailPattern)]],
      requireTwoFactor: [this.requireTwoFactor, [Validators.required]],
      securityApprovalMethod: [this.securityApprovalMethod, [Validators.required]],
      enableSsdPwdBackup: [this.enableSsdPwdBackup, [Validators.required]],
      endpointInactiveInterval: [this.endpointInactiveInterval, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.setTabFromParams();
    this.restConfig$ = this.restConfigService.getRestConfig();
  }

  setTabFromParams() {
    this.route.paramMap.subscribe(params => {
      const tab = params.get('tab');
      if (tab?.toLowerCase() === 'advanced') {
          this.selectedTab = 6;
          setTimeout(() => {
              this.ripple?.launch({
                  centered: true,
                  animation: {
                      enterDuration: 500,
                      exitDuration: 1000
                  }
              });
              setTimeout(() => {
                  if (this.ripple) {
                      this.ripple.disabled = true;
                  }
              }, 0);
          }, 1500);
      }
      else if (tab?.toLowerCase() === 'sensors') {
          this.selectedTab = 1;
      } else {
        setTimeout(() => {
          if (this.ripple) {
            this.ripple.disabled = true;
          }
        }, 1500);
      }
    });
  }

  onEnableSubscriptionKeyChanged(setting: string, checked: boolean) {
    if (setting === 'enable_subscription_key' && checked === false) {
      const title = this.fDisableKeyTitle;
      const content = this.fDisableKeyContent;
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: title,
          content: content,
          rejectBtnName: this.fNo,
          acceptBtnName: this.fYes,
          showRejectBtn: true,
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.enableSubscriptionKey = checked;
          this.isDirty = true;
        } else {
          this.enableSubscriptionKeyControl.checked = !checked;
          this.enableSubscriptionKey = !checked;
        }
      });
    } else {
      this.enableSubscriptionKey = checked;
      this.isDirty = true;
    }

  }

  onToggleSetting(toggleName: string, checked: boolean) {
    // Note for the variable enableSignOnRestriction it is the opposite of enable_permissive_decryption in API
    if (typeof this[toggleName] !== 'undefined') {
      this[toggleName] = checked;
      this.isDirty = true;
    }
  }

  getAdminSettings() {
    const selectedOrgId = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.adminSettingsService
      .getAdminSettings(selectedOrgId)
        .pipe(
            finalize(() => this.settingsDataLoaded = true),
            untilDestroyed(this)
        ).subscribe(
          settings => {
            if (settings) {
              this.adminSettings = settings;
              this.allAdminSettingsViewable = false;
              this.isDirty = false;
              this.orgFormGroup.controls['orgName'].setValue(settings.company_name);
              this.orgFormGroup.controls['contactEmail'].setValue(settings.company_contact_email);
              this.orgFormGroup.controls['requireTwoFactor'].setValue(settings.require_two_factor);
              this.orgFormGroup.controls['securityApprovalMethod'].setValue(settings.security_approval_method);
              this.endpointInactiveIntervalDefault = settings.max_endpoint_inactivity_days_default;
              const inactivityInterval = settings.max_endpoint_inactivity_days === null ?
                  settings.max_endpoint_inactivity_days_default :
                  settings.max_endpoint_inactivity_days;
              this.useInactiveSystemDefault = settings.max_endpoint_inactivity_days === null;
              this.endpointInactiveInterval = inactivityInterval;
              this.endpointInactiveIntervalMin = settings.endpointInactiveIntervalMin || 30;
              this.endpointInactiveIntervalMax = settings.endpointInactiveIntervalMax || 10000;

              this.setShowVars(null);

              this.showDeleteOrgButton = false;
              if (this.isCustomer()) {
                this.allAdminSettingsViewable = true;
                this.showDeleteOrgButton = true;
                this.setShowVars(settings);

                this.enableSubscriptionKey = settings.enable_subscription_key;
                this.requireKeyPassword = settings.require_key_password;
                this.enableRecoveryKey = settings.enable_recovery_key;
                this.enableSignOnRestriction = !settings.enable_permissive_decryption;
                this.enableAutoImport = settings.auto_import_org_public_keys ?? false;
                this.enableRemovableStorage = settings.enable_removable_storage ?? false;
                this.enableClearRemovableStorage = settings.user_can_clear_usb ?? false;
                this.enableDataDeception = settings.enable_data_deception ?? false;
                this.enableClearDataDeception = settings.user_can_clear_data_deception ?? false;
                this.enableNetworkDeception = settings.enable_network_deception ?? false;
                this.enableClearNetworkDeception = settings.user_can_clear_net_deception ?? false;
                this.enableTrustedNetwork = settings.enable_trusted_network ?? false;
                this.enableClearTrustedNetwork = settings.user_can_clear_trusted_net ?? false;
                this.enableAVTethering = settings.enable_antivirus_tethering ?? false;
                this.enableMfaOtp = settings.enable_mfa_otp ?? false;
                this.enableMfaFinger = settings.enable_mfa_finger ?? false;
                this.enableMfaFace = settings.enable_mfa_face ?? false;
                this.enableMfaCac = settings.enable_mfa_cac ?? false;
                this.enableAutoUpdate = settings.enable_auto_update ?? false;
                this.enableClientUpdate = settings.enable_client_update ?? false;
                this.enableSingleUserMode = settings.enable_single_user ?? false;
                this.enableSsdPwdBackup = settings.enable_disk_config ?? false;
                this.enableLimitAuthTrys = settings.enable_limit_auth_trys ?? false;
                this.loginAttempts = settings.login_attempts;
                this.pwExpInterval = this.convertDaysToMonths(settings.pw_exp_interval);
                this.mfaPwExpInterval = this.convertDaysToMonths(settings.mfa_pw_exp_interval);
              }
            }
        }
      );
  }

  private setShowVars(settings: IAdminSettings) {
    this.showRemovableStorage = settings?.show_enable_removable_storage ?? false;
    this.showEnableClearRemovableStorage = settings?.show_user_can_clear_usb ?? false;
    this.showEnableDataDeception = settings?.show_enable_data_deception ?? false;
    this.showEnableClearDataDeception = settings?.show_user_can_clear_data_deception ?? false;
    this.showEnableNetworkDeception = settings?.show_enable_network_deception ?? false;
    this.showEnableClearNetworkDeception = settings?.show_user_can_clear_net_deception ?? false;
    this.showEnableTrustedNetwork = settings?.show_enable_trusted_network ?? false;
    this.showEnableClearTrustedNetwork = settings?.show_user_can_clear_trusted_net ?? false;
    this.showEnableMfaCac = settings?.show_enable_mfa_cac ?? false;
    this.showEnableAutoUpdate = settings?.show_enable_auto_update ?? false;
    this.showEnableSingleUserMode = settings?.show_enable_single_user ?? false;
    this.showEnableLimitAuthTrys = settings?.show_enable_limit_auth_trys ?? false;
    this.showEnableMfaFinger = settings?.show_enable_mfa_finger ?? false;
    this.showEnableMfaOtp = settings?.show_enable_mfa_otp ?? false;
    this.showEnableMfaFace = settings?.show_enable_mfa_face ?? false;
    this.showEnableDataDeception = settings?.show_enable_data_deception ?? false;
  }

  convertDaysToMonths(days) {
    let months = 1;

    if (typeof days === 'number' && days > 0) {
      months = Math.ceil(days / 31);
      months = months > 12 ? 12 : months;
    }

    return months;
  }

  convertMonthsToDays(months: number) {
    let days = 31 * months;
    days = days > 366 ? 366 : days;

    return days;
  }

  getSelectedOrg() {
    let getOrg: Observable<IGroup>;
    if (this.selectedOrgId === 'All') {
      getOrg = this.userService.get_organization(authHelper.getGroupFromToken());
    } else {
      getOrg = this.userService.get_organization(+this.selectedOrgId);
    }
    getOrg
      .pipe(untilDestroyed(this))
      .subscribe(selectedOrg => {
        this.selectedOrg = selectedOrg;
        this.getAdminSettings();
    });
  }

  isCustomer() {
    return this.selectedOrg.org_type.valueOf() === OrgType.CUSTOMER;
  }

  onPassLockCountChanged(event) {
    if (event !== this.loginAttempts) {
      this.isDirty = true;
      this.loginAttempts = event;
    }
  }

  onPassIntervalChanged(event) {
    if (event !== this.pwExpInterval) {
      this.isDirty = true;
      this.pwExpInterval = event;
    }
  }

  onMfaPassIntervalChanged(event) {
    if (event !== this.mfaPwExpInterval) {
      this.isDirty = true;
      this.mfaPwExpInterval = event;
    }
  }

  onDefaultCheckChanged(event) {
    this.isDirty = true;
    this.useInactiveSystemDefault = event.checked;
    this.endpointInactiveInterval = this.endpointInactiveIntervalDefault;
  }

  onInactiveIntervalChanged(value) {
    if (value !== this.endpointInactiveInterval) {
      this.isDirty = true;
      this.endpointInactiveInterval = value;
    }
  }

  onSecurityApprovalMethodChanged() {
    this.isDirty = true;
  }

  onRequire2FAChanged() {
    this.isDirty = true;
  }

  onCompanyChange() {
      this.isDirty = true;
  }

  onContactChange() {
      this.isDirty = true;
  }

  onCancel() {
     this.getAdminSettings();
  }

  onSubmit() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.isDirty = false; // disable the submit key
    const adminSettings: IAdminSettings = {
      enable_subscription_key: this.enableSubscriptionKey,
      require_key_password: this.requireKeyPassword,
      enable_recovery_key: this.enableRecoveryKey,
      enable_permissive_decryption: !this.enableSignOnRestriction,
      auto_import_org_public_keys: this.enableAutoImport,
      enable_removable_storage: this.enableRemovableStorage,
      user_can_clear_usb: this.enableClearRemovableStorage,
      enable_data_deception: this.enableDataDeception,
      user_can_clear_data_deception: this.enableClearDataDeception,
      enable_network_deception: this.enableNetworkDeception,
      user_can_clear_net_deception: this.enableClearNetworkDeception,
      enable_trusted_network: this.enableTrustedNetwork,
      user_can_clear_trusted_net: this.enableClearTrustedNetwork,
      enable_mfa_otp: this.enableMfaOtp,
      enable_mfa_finger: this.enableMfaFinger,
      enable_mfa_face: this.enableMfaFace,
      enable_mfa_cac: this.enableMfaCac,
      enable_auto_update: this.enableAutoUpdate,
      enable_client_update: this.enableClientUpdate,
      enable_single_user: this.enableSingleUserMode,
      enable_disk_config: this.enableSsdPwdBackup,
      enable_limit_auth_trys: this.enableLimitAuthTrys,
      enable_antivirus_tethering: this.enableAVTethering,
      login_attempts: this.loginAttempts,
      pw_exp_interval: this.convertMonthsToDays(this.pwExpInterval),
      mfa_pw_exp_interval: this.convertMonthsToDays(this.mfaPwExpInterval),
      company_name: this.orgFormGroup.controls['orgName'].value,
      company_contact_email: this.orgFormGroup.controls['contactEmail'].value,
      require_two_factor: this.orgFormGroup.controls['requireTwoFactor'].value,
      security_approval_method: this.orgFormGroup.controls['securityApprovalMethod'].value,
      max_endpoint_inactivity_days: this.useInactiveSystemDefault ? -1 : this.endpointInactiveInterval
    };
    this.adminSettingsService.updateAdminSetting(adminSettings, selectedOrg)
        .subscribe(() => {
            this.notification.showSuccess(this.fSuccess);
            this.getAdminSettings();
            this.store.dispatch(loadOrganizations())
          },
          () => {
            this.notification.showError(this.translate.instant('ADMIN_SETTINGS_ERROR'));
            this.getAdminSettings();
          }
    );
    this.securityRequestService.found$.next(false);
  }

  submitDeleteRequest() {
    this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: this.translate.instant('delete-data-request.submit-title'),
        content: this.translate.instant('delete-data-request.submit-content'),
        rejectBtnName: this.translate.instant('NO'),
        acceptBtnName: this.translate.instant('YES'),
        showRejectBtn: true,
      },
    }).afterClosed().subscribe(result => {
      if (result) {
        let organizationId;
        if (this.selectedOrgId === 'All') {
          organizationId = authHelper.getGroupFromToken().toString();
        } else {
          organizationId = this.selectedOrgId;
        }
        this.userService.send_delete_organization_request(organizationId)
        .subscribe(() => {
          this.disableSubmit = true;
          this.notification.showSuccess(this.translate.instant('delete-data-request.submitted'));
        });
      }
    });
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  getIsDirty(): boolean {
    return this.isDirty;
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  userValueChanged(): void {
    // no-op
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  canDeactivateResult(result: boolean): void {
    // no-op
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadComponent() {
    return !this.isDirty;
  }

}
